import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { useT } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Leaf, ArrowLeft, UserX } from 'lucide-react';

export default function Auth() {
    const navigate = useNavigate();
    const { loginEmail, registerEmail, registerAnonymous } = useAuth();
    const { t, lang } = useT();
    const [tab, setTab] = useState('login');
    const brand = lang === 'en' ? 'Stillroom' : 'Stillerom';

    const [loginEmailV, setLoginEmailV] = useState('');
    const [loginPass, setLoginPass] = useState('');
    const [regEmail, setRegEmail] = useState('');
    const [regAlias, setRegAlias] = useState('');
    const [regPass, setRegPass] = useState('');
    const [busy, setBusy] = useState(false);

    const afterAuth = async () => {
        const pending = sessionStorage.getItem('pending_session');
        if (pending) {
            sessionStorage.removeItem('pending_session');
            try {
                const { api } = await import('../lib/api');
                const data = JSON.parse(pending);
                const res = await api.post('/chat/sessions', data);
                navigate(`/app/samtale/${res.data.session.session_id}`);
                return;
            } catch (err) {
                const detail = err?.response?.data?.detail;
                if (typeof detail === 'string') toast.error(detail);
            }
        }
        navigate('/app');
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await loginEmail(loginEmailV, loginPass);
            toast.success(lang === 'en' ? 'Welcome back' : 'Velkommen tilbake');
            await afterAuth();
        } catch (err) {
            toast.error(err.response?.data?.detail || (lang === 'en' ? 'Could not sign in' : 'Kunne ikke logge inn'));
        } finally { setBusy(false); }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        if (regPass.length < 6) { toast.error(lang === 'en' ? 'Password must be at least 6 characters' : 'Passord må være minst 6 tegn'); return; }
        setBusy(true);
        try {
            await registerEmail(regEmail, regPass, regAlias);
            toast.success(lang === 'en' ? 'Account created' : 'Konto opprettet');
            await afterAuth();
        } catch (err) {
            toast.error(err.response?.data?.detail || (lang === 'en' ? 'Could not register' : 'Kunne ikke registrere'));
        } finally { setBusy(false); }
    };

    const handleAnonymous = async () => {
        const confirmed = window.confirm(
            lang === 'en'
                ? ('Anonymous access:\n\n' +
                   '• No email, no password — 100% anonymous\n' +
                   '• 3 days of full access (Premium)\n' +
                   '• The account lives ONLY on this device. If you sign out or clear your browser, the conversations are gone.\n' +
                   '• You can link an email later (in Account settings) without losing history.\n\n' +
                   'Continue?')
                : ('Anonym tilgang:\n\n' +
                   '• Ingen e-post, ingen passord – 100% anonymt\n' +
                   '• 3 dager full tilgang (Premium)\n' +
                   '• Kontoen finnes KUN på denne enheten. Hvis du logger ut eller tømmer nettleseren, er samtalene borte.\n' +
                   '• Du kan når som helst koble på en e-post senere (i Konto-innstillingene) uten å miste historikken.\n\n' +
                   'Vil du fortsette?')
        );
        if (!confirmed) return;
        setBusy(true);
        try {
            await registerAnonymous();
            toast.success(lang === 'en' ? 'Anonymous access — 3 days Premium' : 'Anonym tilgang aktivert – 3 dager Premium');
            await afterAuth();
        } catch (err) {
            toast.error(err.response?.data?.detail || (lang === 'en' ? 'Could not start anonymous access' : 'Kunne ikke starte anonym tilgang'));
        } finally { setBusy(false); }
    };

    const loginWithGoogle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + '/app';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="min-h-screen bg-bone text-ink flex flex-col">
            <header className="max-w-5xl mx-auto w-full px-6 py-6 flex items-center justify-between">
                <button onClick={() => navigate('/')} className="flex items-center gap-2 text-sm text-inkSoft hover:text-ink" data-testid="back-btn">
                    <ArrowLeft className="w-4 h-4" /> {lang === 'en' ? 'Back' : 'Tilbake'}
                </button>
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-sage flex items-center justify-center">
                        <Leaf className="w-4 h-4 text-bone" />
                    </div>
                    <span className="font-display font-bold tracking-tight">{brand}</span>
                </div>
            </header>

            <main className="flex-1 flex items-center justify-center px-6 pb-16">
                <div className="w-full max-w-md">
                    <h1 className="font-display text-3xl font-bold tracking-tight">
                        {lang === 'en' ? 'Welcome in.' : 'Velkommen inn.'}
                    </h1>
                    <p className="mt-2 text-inkSoft">
                        {lang === 'en' ? 'Use your name. Or don\u2019t.' : 'Du velger selv om du vil bruke navn eller ikke.'}
                    </p>

                    <div className="mt-8 rounded-2xl border border-sand bg-white p-6">
                        <Tabs value={tab} onValueChange={setTab}>
                            <TabsList className="grid grid-cols-2 w-full bg-sand/40" data-testid="auth-tabs">
                                <TabsTrigger value="login" data-testid="tab-login">{t('auth.signin')}</TabsTrigger>
                                <TabsTrigger value="register" data-testid="tab-register">{lang === 'en' ? 'New here' : 'Ny her'}</TabsTrigger>
                            </TabsList>

                            <TabsContent value="login" className="mt-6">
                                <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                                    <div>
                                        <Label htmlFor="li-email">{t('auth.email')}</Label>
                                        <Input id="li-email" type="email" required value={loginEmailV} onChange={e => setLoginEmailV(e.target.value)} data-testid="login-email-input" />
                                    </div>
                                    <div>
                                        <Label htmlFor="li-pw">{t('auth.password')}</Label>
                                        <Input id="li-pw" type="password" required value={loginPass} onChange={e => setLoginPass(e.target.value)} data-testid="login-password-input" />
                                    </div>
                                    <Button type="submit" disabled={busy} className="w-full rounded-full bg-sage hover:bg-sage-dark text-bone h-11" data-testid="login-submit-btn">
                                        {busy ? (lang === 'en' ? 'Signing in…' : 'Logger inn…') : t('auth.signin')}
                                    </Button>
                                </form>
                            </TabsContent>

                            <TabsContent value="register" className="mt-6">
                                <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                                    <div>
                                        <Label htmlFor="r-alias">{lang === 'en' ? 'Alias' : 'Alias'} <span className="text-inkSoft">{lang === 'en' ? '(optional)' : '(valgfritt)'}</span></Label>
                                        <Input id="r-alias" type="text" placeholder={lang === 'en' ? 'e.g. Fox-12' : 'F.eks. Reve-12'} value={regAlias} onChange={e => setRegAlias(e.target.value)} data-testid="register-alias-input" />
                                    </div>
                                    <div>
                                        <Label htmlFor="r-email">{t('auth.email')}</Label>
                                        <Input id="r-email" type="email" required value={regEmail} onChange={e => setRegEmail(e.target.value)} data-testid="register-email-input" />
                                    </div>
                                    <div>
                                        <Label htmlFor="r-pw">{t('auth.password')} <span className="text-inkSoft">{lang === 'en' ? '(min. 6 chars)' : '(min. 6 tegn)'}</span></Label>
                                        <Input id="r-pw" type="password" required minLength={6} value={regPass} onChange={e => setRegPass(e.target.value)} data-testid="register-password-input" />
                                    </div>
                                    <Button type="submit" disabled={busy} className="w-full rounded-full bg-sage hover:bg-sage-dark text-bone h-11" data-testid="register-submit-btn">
                                        {busy ? (lang === 'en' ? 'Creating…' : 'Oppretter…') : t('auth.create.account')}
                                    </Button>
                                </form>
                            </TabsContent>
                        </Tabs>

                        <div className="my-6 flex items-center gap-3 text-xs text-inkSoft">
                            <div className="h-px bg-sand flex-1"></div>
                            <span>{t('auth.or')}</span>
                            <div className="h-px bg-sand flex-1"></div>
                        </div>

                        <Button
                            onClick={loginWithGoogle}
                            variant="outline"
                            className="w-full rounded-full border-sand hover:bg-sand/30 h-11"
                            data-testid="google-login-btn"
                        >
                            <GoogleIcon /> {t('auth.continue.google')}
                        </Button>

                        <Button
                            onClick={handleAnonymous}
                            disabled={busy}
                            variant="outline"
                            className="mt-3 w-full rounded-full border-sand hover:bg-sand/30 h-11"
                            data-testid="anonymous-btn"
                        >
                            <UserX className="w-4 h-4 mr-2"/>
                            {busy ? (lang === 'en' ? 'Starting…' : 'Starter…') : t('auth.continue.anonymous')}
                        </Button>
                        <p className="mt-2 text-[11px] text-inkSoft leading-snug">
                            {t('auth.anonymous.subtle')}
                        </p>
                    </div>

                    <p className="text-xs text-inkSoft mt-6 text-center leading-relaxed">
                        {lang === 'en'
                            ? <>By continuing you accept our <Link to="/vilkar" className="text-sage-dark underline" data-testid="auth-terms-link">terms</Link> and <Link to="/personvern" className="text-sage-dark underline" data-testid="auth-privacy-link">privacy policy</Link>.<br/>We only store what's needed to have conversations.</>
                            : <>Ved å fortsette godtar du våre <Link to="/vilkar" className="text-sage-dark underline" data-testid="auth-terms-link">vilkår</Link> og <Link to="/personvern" className="text-sage-dark underline" data-testid="auth-privacy-link">personvernerklæring</Link>.<br/>Vi lagrer kun det du trenger for å ha samtaler.</>}
                    </p>
                </div>
            </main>
        </div>
    );
}

function GoogleIcon() {
    return (
        <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
        </svg>
    );
}
